import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createStreamConfigApi, isStreamConfigForbidden } from '@bloo-team/api-client'
import type { Stream, StreamConfig } from '@bloo-team/api-client'
import { client, getSession, identityFor, isDemoSession } from '@bloo-team/core-shell'
import { storageStore, useStorageOverlay, type RetentionLevel, type StorageOverlay } from './storage-store'

/**
 * Retention — the stream-config model after the live capture
 * (docs/inventory/storage-capture.json R1GET / R1PUT / R2).
 *
 * Retention and replication are NOT per stream. `GET mgmt/core/stream-config`
 * answers ONE cluster-wide `{ retention, replication }`; that is why every
 * stream-management row reports 90/1. An optional scope override lives at
 * `mgmt/scope/{scopeId}/stream-config`. So the model is:
 *
 *   effective = scope override ?? cluster default
 *   every stream row shows the effective value (stream-management)
 *
 * and a row that reports something else is DRIFT — `retentionSummary`
 * counts it, the tab chips it. It should be 0 today; it surfaces the day
 * the backend diverges.
 *
 * Writes and the scope read were refused for an Organization Admin /
 * Security Engineer (`user not authorised`, a 200 body). They need Root or
 * TenantAdmin (CB-33, product owner) — so `canEditClusterRetention` gates
 * the editors on that, and the body shape (`{ retention, replication }`
 * from the legacy openapi, no stream) stays UNTESTED: the Apply buttons are
 * rendered, disabled and labelled outside demo (`RETENTION_GATES`). Demo mode
 * writes the overlay (`storage-store.ts`, two level slots) and the reads
 * apply it, so the page has one code path.
 */

export type { StreamConfig, RetentionLevel }

export const streamConfigApi = createStreamConfigApi(client)

/* ------------------------------------------------------------------ gates */

/** Capture gate per level (docs/storage-requirements.md §Capture ledger). */
export const RETENTION_GATES = { cluster: 'R1PUT', scope: 'R2' } as const

const GATE_ENDPOINT: Record<RetentionLevel, string> = {
  cluster: 'PUT mgmt/core/stream-config',
  scope: 'PUT mgmt/scope/{scopeId}/stream-config',
}

/** The disabled Apply's wrapper title / the note under the card. */
export function retentionGateReason(level: RetentionLevel): string {
  return `Write shape unconfirmed — capture ${RETENTION_GATES[level]} (${GATE_ENDPOINT[level]}) pending (docs/storage-requirements.md §Capture ledger)`
}

/** Who the server let through: neither an Organization Admin nor a Security Engineer (R1PUT, R2). */
export const RETENTION_ROLE_NOTE = 'Root or TenantAdmin — R1PUT/R2 pending from such a session'

/** The scope card when the read itself was refused. */
export const SCOPE_FORBIDDEN_NOTE = 'needs a higher role — Root or TenantAdmin (R2)'

/**
 * Who may edit either level: Root, or the session's backend `isAdmin` flag —
 * the tenant-admin bit the legacy role string spells `TenantAdmin`. NOT the
 * console's derived `Identity.isTenantAdmin`: that folds every Organization
 * Admin in, and an Organization Admin / Security Engineer is exactly who the
 * capture refused (R1PUT, R2). A UX affordance only — authorization is the
 * server's, and a refusal comes back as `StreamConfigForbidden`. Which flag
 * the backend really keys on is confirmed the day R1PUT/R2 run from such a
 * session (CB-33).
 */
export function canEditClusterRetention(clusterId: string, scopeId: string): boolean {
  const session = getSession()
  if (!session) return false
  const id = identityFor(session, clusterId, scopeId)
  return id.userType === 'Root' || session.user.isAdmin
}

/** Same rule at the scope level — one role decision for both cards. */
export const canEditScopeRetention = canEditClusterRetention

/* --------------------------------------------------------------- fixtures */

/** R1GET verbatim — and what every `DEMO_STREAMS` row carries. */
export const DEMO_CLUSTER_STREAM_CONFIG: StreamConfig = { retention: 90, replication: 1 }

/** No override in the demo tenant: the cluster default applies. */
export const DEMO_SCOPE_STREAM_CONFIG: StreamConfig | null = null

/* ------------------------------------------------------------------ model */

/** The scope level: an override, none, or a read the server refused. */
export type ScopeStreamConfig = StreamConfig | null | 'forbidden'

export interface StreamConfigLevels {
  cluster: StreamConfig | null
  scope: ScopeStreamConfig
}

const isConfig = (v: ScopeStreamConfig | StreamConfig | null): v is StreamConfig => typeof v === 'object' && v !== null

/** `scope ?? cluster`, field by field — a partial override falls back per field. */
export function effectiveRetention(cluster: StreamConfig | null, scope: ScopeStreamConfig): StreamConfig | null {
  const s = isConfig(scope) ? scope : null
  if (!s) return cluster
  if (!cluster) return s
  return { retention: s.retention ?? cluster.retention, replication: s.replication ?? cluster.replication }
}

/** A row whose reported value differs from the effective one (null effective fields are not compared). */
export function rowDrifts(s: Pick<Stream, 'retention' | 'replication'>, effective: StreamConfig | null): boolean {
  if (!effective) return false
  return (
    (effective.retention !== null && s.retention !== effective.retention) ||
    (effective.replication !== null && s.replication !== effective.replication)
  )
}

export interface RetentionSummary {
  effective: StreamConfig | null
  /** Rows whose retention / replication differ from `effective` — 0 today; drift if the backend diverges. */
  streamsDiffering: number
}

/** The strip's retention figure: one effective value and the drift count. */
export function retentionSummary(streams: Stream[], config: StreamConfigLevels): RetentionSummary {
  const effective = effectiveRetention(config.cluster, config.scope)
  return { effective, streamsDiffering: streams.filter((s) => rowDrifts(s, effective)).length }
}

/**
 * The demo twin of stream-management reflecting a config write: every row
 * takes the effective value (non-null fields only). Untouched rows keep
 * their identity so memoised consumers do not re-render.
 */
export function applyEffectiveRetention(streams: Stream[], effective: StreamConfig | null): Stream[] {
  if (!effective) return streams
  return streams.map((s) => {
    const retention = effective.retention ?? s.retention
    const replication = effective.replication ?? s.replication
    return retention === s.retention && replication === s.replication ? s : { ...s, retention, replication }
  })
}

/** Fixtures with the overlay's level slots in place. */
export function applyRetentionOverlay(base: StreamConfigLevels, overlay: StorageOverlay): StreamConfigLevels {
  const pick = (o: { retention: number | null; replication: number | null }): StreamConfig => ({
    retention: o.retention,
    replication: o.replication,
  })
  const { cluster, scope } = overlay.retention
  return {
    cluster: cluster ? pick(cluster) : base.cluster,
    scope: scope ? pick(scope) : base.scope,
  }
}

/* ------------------------------------------------------------------ reads */

interface Scope {
  clusterId: string
  scopeId: string
}

const mode = () => (isDemoSession() ? 'demo' : 'live')

/** `['stream-config', clusterId, …]` — invalidating that prefix refreshes both levels. */
export const streamConfigKey = (clusterId: string, scopeId?: string) =>
  scopeId
    ? (['stream-config', clusterId, 'scope', scopeId, mode()] as const)
    : (['stream-config', clusterId, 'cluster', mode()] as const)

export interface StreamConfigView extends StreamConfigLevels {
  /** The cluster read — the one the page cannot do without. */
  isPending: boolean
  isError: boolean
  error: Error | null
  isFetching: boolean
  /** The scope read is tolerated: a refusal is `scope: 'forbidden'`, any other failure lands here with `scope: null`. */
  scopePending: boolean
  scopeError: Error | null
}

/**
 * Both levels. Demo: the fixtures under the overlay. Live: two queries; the
 * scope one turns `StreamConfigForbidden` into the `'forbidden'` value (data,
 * not an error — the tab renders the role note) and tolerates anything else.
 */
export function useStreamConfig({ clusterId, scopeId }: Scope): StreamConfigView {
  const isDemo = isDemoSession()
  const overlay = useStorageOverlay(clusterId, scopeId)

  const clusterQ = useQuery<StreamConfig | null>({
    queryKey: streamConfigKey(clusterId),
    queryFn: async () => (isDemo ? DEMO_CLUSTER_STREAM_CONFIG : streamConfigApi.get({ clusterId })),
    retry: 1,
  })
  const scopeQ = useQuery<ScopeStreamConfig>({
    queryKey: streamConfigKey(clusterId, scopeId),
    queryFn: async () => {
      if (isDemo) return DEMO_SCOPE_STREAM_CONFIG
      try {
        return await streamConfigApi.getScope({ clusterId, scopeId })
      } catch (e) {
        if (isStreamConfigForbidden(e)) return 'forbidden'
        throw e
      }
    },
    retry: 1,
  })

  const levels = useMemo<StreamConfigLevels>(() => {
    const base: StreamConfigLevels = { cluster: clusterQ.data ?? null, scope: scopeQ.data ?? null }
    return isDemo ? applyRetentionOverlay(base, overlay) : base
  }, [clusterQ.data, scopeQ.data, isDemo, overlay])

  return {
    ...levels,
    isPending: clusterQ.isPending,
    isError: clusterQ.isError,
    error: clusterQ.error,
    isFetching: clusterQ.isFetching || scopeQ.isFetching,
    scopePending: scopeQ.isPending,
    scopeError: scopeQ.error,
  }
}

/* ----------------------------------------------------------------- writes */

function useInvalidate({ clusterId, scopeId }: Scope) {
  const qc = useQueryClient()
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['stream-config', clusterId] }),
      qc.invalidateQueries({ queryKey: ['streams', clusterId, scopeId] }),
    ])
  }
}

/**
 * Write the cluster default — `PUT mgmt/core/stream-config { retention,
 * replication }` (R1PUT, UNTESTED; refused for a Security Engineer, so the
 * error may be `StreamConfigForbidden`). Demo: the overlay's `cluster` slot.
 */
export function useSetClusterRetention(scope: Scope) {
  const invalidate = useInvalidate(scope)
  return useMutation<void, Error, StreamConfig>({
    mutationFn: async (values) => {
      if (isDemoSession()) {
        storageStore(scope.clusterId, scope.scopeId).setRetention('cluster', values)
        return
      }
      await streamConfigApi.put({ clusterId: scope.clusterId, ...values })
    },
    onSuccess: invalidate,
  })
}

/** Write the scope override — `PUT mgmt/scope/{scopeId}/stream-config` (R2, UNTESTED). Demo: the overlay's `scope` slot. */
export function useSetScopeRetention(scope: Scope) {
  const invalidate = useInvalidate(scope)
  return useMutation<void, Error, StreamConfig>({
    mutationFn: async (values) => {
      if (isDemoSession()) {
        storageStore(scope.clusterId, scope.scopeId).setRetention('scope', values)
        return
      }
      await streamConfigApi.putScope({ clusterId: scope.clusterId, scopeId: scope.scopeId, ...values })
    },
    onSuccess: invalidate,
  })
}
